import * as React from 'react';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import axios from 'axios';
import { useState } from "react";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { API_URL } from "../config";
import { useDispatch } from "react-redux";
import { setAccess, setMe, resetSessionPolicy } from "../redux/sessionSlice";
import logo from '../assets/digifab-logo-siyah.png';
import { useNavigate } from "react-router-dom";
import '../App.css';

function Copyright(props) {
    return (
        <Typography variant="body2" color="text.secondary" align="center" {...props}>
            {'Copyright © '}
            <Link color="inherit" href="#" underline="hover">
                DigiFab
            </Link>{' '}
            {new Date().getFullYear()}
        </Typography>
    );
}

const theme = createTheme({
    palette: {
        primary: {
            main: '#6366f1',
        },
        secondary: {
            main: '#8b5cf6',
        },
    },
});

export default function Login(props) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [loginForm, setLoginform] = useState({
        username: "",
        password: "",
    });

    const onChangeForm = (label, event) => {
        switch (label) {
            case "username":
                setLoginform({ ...loginForm, username: event.target.value });
                break;
            case "password":
                setLoginform({ ...loginForm, password: event.target.value });
                break;
            default:
                break;
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);

        try {
            const response = await axios.post(`${API_URL}/auth/token`, data, {
                withCredentials: true,
            });

            const token = response.data.access_token;
            const exp = response.data.exp;
            const mustChangePassword = Boolean(response.data.must_change_password);

            dispatch(resetSessionPolicy());
            dispatch(setAccess({ token, exp }));

            try {
                const payload = JSON.parse(atob(token.split(".")[1]));
                const userInfo = {
                    email: payload.sub || payload.email || "",
                    full_name: payload.full_name || "",
                    global_role: payload.global_role || null,
                    tenant_role: payload.tenant_role || null,
                    must_change_password: mustChangePassword,
                };
                dispatch(setMe(userInfo));
                localStorage.setItem("user_info", JSON.stringify({
                    full_name: payload.full_name || "",
                }));
            } catch (e) {
                console.error("Token decode hatası:", e);
            }
            toast.success(mustChangePassword ? "İlk giriş: lütfen şifrenizi değiştirin" : "Giriş başarılı");
            navigate(mustChangePassword ? "/change-password-required" : "/home");
        } catch (error) {
            const status = error?.response?.status;
            const detail = error?.response?.data?.detail;
            const message = typeof detail === "string"
                ? detail
                : Array.isArray(detail) && detail[0]?.msg
                    ? detail[0].msg
                    : status === 401
                        ? "E-posta veya şifre hatalı"
                        : "Giriş başarısız";
            toast.error(message);
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    padding: 2,
                }}
            >
                <CssBaseline />
                <Container component="main" maxWidth="sm">
                    <Paper
                        elevation={24}
                        sx={{
                            padding: { xs: 3, sm: 5 },
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            borderRadius: 3,
                            backgroundColor: 'rgba(255, 255, 255, 0.98)',
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        {/* Logo */}
                        <Box
                            component="img"
                            src={logo}
                            alt="DigiFab Logo"
                            sx={{
                                width: '60%',
                                maxWidth: '250px',
                                height: 'auto',
                                marginBottom: 2,
                            }}
                        />

                        {/* Title */}
                        <Typography
                            component="h1"
                            variant="h5"
                            sx={{
                                fontWeight: 700,
                                color: '#1f2937',
                                marginBottom: 1,
                                textAlign: 'center',
                            }}
                        >
                            DigiFab IoT Platformu
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{
                                color: '#6b7280',
                                marginBottom: 4,
                                textAlign: 'center',
                            }}
                        >
                            Hesabınıza giriş yapın
                        </Typography>

                        {/* Form */}
                        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="username"
                                label="E-posta"
                                name="username"
                                autoComplete="username"
                                autoFocus
                                onChange={(event) => onChangeForm("username", event)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    },
                                }}
                            />
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label="Şifre"
                                type="password"
                                id="password"
                                autoComplete="current-password"
                                onChange={(event) => onChangeForm("password", event)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    },
                                }}
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{
                                    mt: 3,
                                    mb: 2,
                                    py: 1.5,
                                    borderRadius: 2,
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    boxShadow: '0 4px 14px 0 rgba(99, 102, 241, 0.4)',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    '&:hover': {
                                        boxShadow: '0 6px 20px rgba(99, 102, 241, 0.6)',
                                        background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                                    },
                                }}
                            >
                                Giriş Yap
                            </Button>

                            <Button
                                type="button"
                                fullWidth
                                variant="outlined"
                                sx={{
                                    mb: 2,
                                    py: 1.5,
                                    borderRadius: 2,
                                    fontSize: '15px',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    borderWidth: 2,
                                    '&:hover': {
                                        borderWidth: 2,
                                        backgroundColor: 'rgba(99, 102, 241, 0.04)',
                                    },
                                }}
                                onClick={() => navigate('/forgot-password')}
                            >
                                Şifremi Unuttum
                            </Button>
                        </Box>

                        <Copyright sx={{ mt: 3 }} />
                    </Paper>
                </Container>
            </Box>
        </ThemeProvider>
    );
}